/**
 * Step indicator for the Wizard component. Renders numbered circles
 * connected by lines, with a completed (check + brand-success) state, an
 * active (filled brand) state, and an upcoming (muted) state. Includes an
 * `aria-live` region that announces step changes for screen readers.
 *
 * Generalized from the previous lib/crm/list/components/shared
 * implementation: no hardcoded step counts, no coupled width assumptions.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { cx } from '@/utils/cx';

export interface WizardStepIndicatorStep {
  id: string;
  label: string;
}

interface WizardStepIndicatorProps {
  steps: WizardStepIndicatorStep[];
  /** Zero-based index of the current step. */
  currentStepIndex: number;
  /**
   * If provided, completed steps render as buttons that call this handler
   * with the clicked step's zero-based index. Upcoming and active steps
   * remain non-interactive.
   */
  onStepClick?: (stepIndex: number) => void;
}

const CIRCLE_CLASSES =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors';

interface StepButtonProps {
  index: number;
  label: string;
  onStepClick: (index: number) => void;
}

function StepButton({ index, label, onStepClick }: StepButtonProps) {
  const handleClick = useCallback(
    () => onStepClick(index),
    [onStepClick, index]
  );

  return (
    <button
      type='button'
      onClick={handleClick}
      className={cx(
        CIRCLE_CLASSES,
        'cursor-pointer hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500',
        'bg-[var(--color-success-600)] text-[var(--color-white)]'
      )}
      aria-label={`Go to step ${index + 1}: ${label}`}
    >
      <svg
        className='h-3 w-3'
        fill='none'
        viewBox='0 0 24 24'
        stroke='currentColor'
        aria-hidden='true'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={3}
          d='M5 13l4 4L19 7'
        />
      </svg>
    </button>
  );
}

function StepGrid({
  templateColumns,
  children,
}: {
  templateColumns: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className='grid items-center gap-y-1'
      style={{ gridTemplateColumns: templateColumns }}
    >
      {children}
    </div>
  );
}

export function WizardStepIndicator({
  steps,
  currentStepIndex,
  onStepClick,
}: WizardStepIndicatorProps) {
  const liveRegionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (liveRegionRef.current) {
      const step = steps[currentStepIndex];
      liveRegionRef.current.textContent = `Step ${currentStepIndex + 1} of ${steps.length}: ${step?.label ?? ''}`;
    }
  }, [currentStepIndex, steps]);

  // Grid columns alternate `auto` (circle) with `1fr` (connecting line).
  // For N steps that's `2N-1` columns: N circles + (N-1) lines between them.
  const gridTemplateColumns = useMemo(
    () =>
      Array.from({ length: 2 * steps.length - 1 }, (_, i) =>
        i % 2 === 0 ? 'auto' : '1fr'
      ).join(' '),
    [steps.length]
  );

  if (steps.length === 0) {
    return null;
  }

  return (
    <>
      <div
        ref={liveRegionRef}
        role='status'
        aria-live='polite'
        className='sr-only'
      />
      <StepGrid templateColumns={gridTemplateColumns}>
        {/* Row 1: circles + connecting lines */}
        {steps.map((step, index) => {
          const isActive = currentStepIndex === index;
          const isCompleted = currentStepIndex > index;

          return (
            <div key={`circle-${step.id}`} className='contents'>
              {/* Connecting line before each non-first step */}
              {index > 0 && (
                <div
                  className={cx(
                    'h-px transition-colors',
                    currentStepIndex >= index
                      ? 'bg-[var(--color-success-600)]'
                      : 'bg-[var(--color-bg-tertiary)]'
                  )}
                  aria-hidden='true'
                />
              )}
              <div className='flex justify-center'>
                {isCompleted && onStepClick ? (
                  <StepButton
                    index={index}
                    label={step.label}
                    onStepClick={onStepClick}
                  />
                ) : (
                  <div
                    className={cx(
                      CIRCLE_CLASSES,
                      isActive
                        ? 'bg-[var(--color-brand-600)] text-[var(--color-white)]'
                        : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)]'
                    )}
                    aria-current={isActive ? 'step' : undefined}
                  >
                    {index + 1}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Row 2: labels — same grid columns, line cells left empty */}
        {steps.map((step, index) => {
          const isActive = currentStepIndex === index;
          return (
            <div key={`label-${step.id}`} className='contents'>
              {index > 0 && <div aria-hidden='true' />}
              <span
                className={cx(
                  'text-xs font-medium text-center',
                  isActive
                    ? 'text-[var(--color-text-primary)]'
                    : 'text-[var(--color-text-tertiary)]'
                )}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </StepGrid>
    </>
  );
}
